import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  getToBeProcessed,
  getContractDateDetail,
  bulkConfirmServiceDates,
} from 'api/requests/contract-processing-requests';
import {
  ToBeProcessedFilters,
  ToBeProcessedItem,
  BulkConfirmServiceDatesRequest,
} from 'modules/contract-processing/types';
import {
  CONTRACT_PROCESSING__GENERAL_LOADING,
  CONTRACT_PROCESSING__GET_TO_BE_PROCESSED,
  CONTRACT_PROCESSING__GET_CONTRACT_DATE_DETAIL,
  CONTRACT_PROCESSING__SET_DATA,
  CONTRACT_PROCESSING__SET_ERROR,
  CONTRACT_PROCESSING__BULK_CONFIRM,
  CONTRACT_PROCESSING__SET_BULK_CONFIRM_ERRORS,
} from 'store/constants';

export const setContractProcessingLoadingAction = createAction<boolean>(CONTRACT_PROCESSING__GENERAL_LOADING);
export const setContractProcessingDataAction =
  createAction<{ items: ToBeProcessedItem[]; totalItems: number }>(CONTRACT_PROCESSING__SET_DATA);
export const setContractProcessingErrorAction = createAction<boolean>(CONTRACT_PROCESSING__SET_ERROR);
export const setBulkConfirmErrorsAction = createAction<Record<number, string>>(
  CONTRACT_PROCESSING__SET_BULK_CONFIRM_ERRORS,
);

export const getToBeProcessedAction = createAsyncThunk(
  CONTRACT_PROCESSING__GET_TO_BE_PROCESSED,
  async (props: { companyId: number; filters: ToBeProcessedFilters }, thunkApi) => {
    thunkApi.dispatch(setContractProcessingErrorAction(false));
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await getToBeProcessed(props.filters, props.companyId);
      thunkApi.dispatch(
        setContractProcessingDataAction({
          items: response.items,
          totalItems: response.numberOfResults,
        }),
      );
      return response;
    } catch (error) {
      thunkApi.dispatch(setContractProcessingErrorAction(true));
      throw error;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);

export const getContractDateDetailAction = createAsyncThunk(
  CONTRACT_PROCESSING__GET_CONTRACT_DATE_DETAIL,
  async (props: { contractDateId: number; companyId: number }, thunkApi) => {
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await getContractDateDetail(props.contractDateId, props.companyId);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return thunkApi.rejectWithValue(403);
      }
      throw error;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);

export const bulkConfirmServiceDatesAction = createAsyncThunk(
  CONTRACT_PROCESSING__BULK_CONFIRM,
  async (props: { request: BulkConfirmServiceDatesRequest; companyId: number }, thunkApi) => {
    thunkApi.dispatch(setContractProcessingLoadingAction(true));
    try {
      const response = await bulkConfirmServiceDates(props.request, props.companyId);
      const errorMap: Record<number, string> = {};
      response.forEach(item => {
        errorMap[item.contractDateId] = item.translationKey;
      });
      thunkApi.dispatch(setBulkConfirmErrorsAction(errorMap));
      return response;
    } finally {
      thunkApi.dispatch(setContractProcessingLoadingAction(false));
    }
  },
);
